import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ProductsSection from './components/ProductsSection';
import IndustrialSection from './components/IndustrialSection';
import PaintingSection from './components/PaintingSection';
import ContactSection from './components/ContactSection';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://sjengineering.co.kr';

const SCHEMA_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'ProfessionalService'],
    '@id': `${SITE_URL}/#organization`,
    name: '(주)에스제이엔지니어링',
    alternateName: ['SJ Engineering Co., Ltd.', '에스제이엔지니어링', '에스제이 엔지니어링'],
    description:
      '경기도 안산 소재 산업기계설비 및 도장설비 제조 전문기업. 2000년 창업 이래 25년 이상의 기술력으로 컨베이어 시스템, 자동화 생산라인, 스프레이 부스, 건조로 등 고객 맞춤형 산업설비를 설계·제작·납품합니다.',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
      width: 200,
      height: 60,
    },
    image: `${SITE_URL}/og-image.png`,
    telephone: '+82-31-404-3790',
    faxNumber: '+82-31-404-6480',
    email: 'info@sjengineering.co.kr',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+82-31-404-3790',
        contactType: 'customer service',
        availableLanguage: '한국어',
      },
      {
        '@type': 'ContactPoint',
        telephone: '+82-10-3790-5664',
        contactType: 'sales',
        availableLanguage: '한국어',
      },
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '엠티브이10로10번길9',
      addressLocality: '안산시 단원구',
      addressRegion: '경기도',
      postalCode: '15656',
      addressCountry: 'KR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 37.3235,
      longitude: 126.8167,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    founder: {
      '@type': 'Person',
      alternateName: 'Se Kyu Her',
      jobTitle: '대표이사',
    },
    foundingDate: '2000',
    numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 10 },
    areaServed: [
      { '@type': 'Country', name: '대한민국' },
      { '@type': 'AdministrativeArea', name: '경기도' },
      { '@type': 'City', name: '안산시' },
    ],
    knowsAbout: [
      '산업기계설비',
      '도장설비',
      '컨베이어 시스템',
      '스프레이 부스',
      '건조로',
      '자동화 생산라인',
      '산업설비 설계',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: '산업설비 제품 카탈로그',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '산업기계설비 제조',
            description: '고객 맞춤형 산업기계설비 설계 및 제작',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '도장설비 제조',
            description: '스프레이 부스, 건조로 등 도장 관련 설비 제조',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '컨베이어 시스템',
            description: '자동화 컨베이어 시스템 설계 및 구축',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '스프레이 부스',
            description: '각종 산업용 스프레이 부스 설계 및 제작',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '건조로',
            description: '산업용 건조로 맞춤 제작',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '맞춤형 산업설비 솔루션',
            description: '고객 요구에 맞춘 종합 산업설비 솔루션 제공',
          },
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: '(주)에스제이엔지니어링',
    description: '안산 산업기계설비 도장설비 제조 전문기업 공식 웹사이트',
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'ko-KR',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    url: SITE_URL,
    name: '안산 산업기계설비 도장설비 제조 | (주)에스제이엔지니어링',
    description:
      '경기도 안산 소재 산업기계설비 및 도장설비 제조 전문기업. 2000년 창업 이래 25년 이상의 기술력으로 최고의 산업설비 솔루션을 제공합니다.',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'ko-KR',
    dateModified: '2026-03-24',
    datePublished: '2000-01-01',
    breadcrumb: { '@id': `${SITE_URL}/#breadcrumb` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.hero-title'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}/#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '홈',
        item: SITE_URL,
      },
    ],
  },
];

export default function Home() {
  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];
    SCHEMA_JSONLD.forEach((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `jsonld-home-${idx}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });
    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSection />
        <AboutSection />
        <ProductsSection />
        <IndustrialSection />
        <PaintingSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
